import { useEffect } from 'react';

import { useAppDispatch } from 'dux/app/hooks';

import { type BoxProps, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { closeAccountToast, closeRootToast } from 'dux/user/actions';

type BasicToastProps = {
  children: React.ReactNode;
  bgcolor?: string;
  textColor?: BoxProps['color'];
  dataTestId?: string;
  isFullWidth?: boolean;
  shouldDisplayToast?: boolean;
  isPermanent?: boolean;
  toastType?: 'root' | 'account';
  shouldStack?: boolean;
};

const DISPLAY_DURATION = 5000;
const ANIMATE_OUT_DURATION = 250;

const ToastContainer = styled.div<{ isFullWidth: boolean }>`
  pointer-events: none;

  position: fixed;
  /* Originally at 1100 but for the first-trial-offer-promo-ab-test we need to put it to this value */
  z-index: 1099;
  top: 95px;
  right: 0;

  overflow: hidden;

  width: 100%;

  &.account-toast-container-stacked {
    top: 141px;
  }

  @media (min-width: 768px) {
    width: ${({ isFullWidth }) => (isFullWidth ? '100%' : 'calc(100vw - 225px)')};
  }

  @media (min-width: 1024px) {
    width: ${({ isFullWidth }) => (isFullWidth ? '100vw' : '80vw')};
  }
`;

const Toast = styled.div<{ isOpen?: boolean }>`
  pointer-events: auto; /* to help cypress 'see' this element */

  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(calc(-100% - 1px))')};

  padding: 14px 24px;

  background-color: var(--bgcolor);

  transition: ${({ isOpen }) =>
    isOpen ? 'transform 500ms' : `transform ${ANIMATE_OUT_DURATION}ms`};
`;

export const BasicToast = ({
  children,
  bgcolor = theme.colors.primary[300],
  textColor = 'neutral.100',
  dataTestId = 'toast-banner',
  isFullWidth = false,
  shouldDisplayToast = false,
  isPermanent = false,
  toastType = 'root',
  shouldStack = false,
}: BasicToastProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (shouldDisplayToast && !isPermanent) {
      timeout = setTimeout(() => {
        if (toastType === 'account') {
          dispatch(closeAccountToast());
        } else {
          dispatch(closeRootToast());
        }
      }, DISPLAY_DURATION);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [shouldDisplayToast, isPermanent, toastType]);

  return (
    <ToastContainer
      className={toastType === 'account' && shouldStack ? 'account-toast-container-stacked' : ''}
      isFullWidth={isFullWidth}
    >
      <Toast data-testid={dataTestId} isOpen={shouldDisplayToast} style={{ '--bgcolor': bgcolor }}>
        <Text color={textColor} textAlign="center" variant="body5regular">
          {children}
        </Text>
      </Toast>
    </ToastContainer>
  );
};
